import type { SavedAddress, DeliveryAddress } from '@/types'
import { HomeIcon, BriefcaseIcon, MapPinIcon, ClockIcon } from '../../../components/ui/icons'

interface SavedAddressesProps {
  savedAddresses: SavedAddress[]
  recentAddresses: DeliveryAddress[]
  onSelect: (address: DeliveryAddress) => void
}

export function SavedAddresses({
  savedAddresses,
  recentAddresses,
  onSelect,
}: SavedAddressesProps) {
  return (
    <div className="flex flex-col">
      {/* Saved Addresses */}
      {savedAddresses.length > 0 && (
        <div>
          <h3 className="px-4 py-2 text-xs/4 font-semibold text-text-secondary uppercase tracking-wide">
            Saved Addresses
          </h3>
          <div className="divide-y divide-border-light">
            {savedAddresses.map((address) => (
              <AddressRow
                key={address.id}
                icon={address.label === 'home' ? <HomeIcon className="size-5" /> : <BriefcaseIcon className="size-5" />}
                title={address.name || address.label}
                subtitle={address.formatted}
                onClick={() => onSelect(address)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent Addresses */}
      {recentAddresses.length > 0 && (
        <div className="mt-4">
          <h3 className="px-4 py-2 text-xs/4 font-semibold text-text-secondary uppercase tracking-wide">
            Recent Addresses
          </h3>
          <div className="divide-y divide-border-light">
            {recentAddresses.map((address, index) => (
              <AddressRow
                key={index}
                icon={<ClockIcon className="size-5" />}
                title={address.street}
                subtitle={`${address.suburb} ${address.state} ${address.postcode}`}
                onClick={() => onSelect(address)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface AddressRowProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  onClick: () => void
}

function AddressRow({ icon, title, subtitle, onClick }: AddressRowProps) {
  return (
    <button
      onClick={onClick}
      className="
        w-full px-4 py-3 flex items-center gap-3
        text-left
        hover:bg-surface-tertiary
        transition-colors
        focus-visible:outline-none focus-visible:bg-surface-tertiary
      "
    >
      <div className="size-10 flex items-center justify-center rounded-lg bg-surface-tertiary text-text-secondary">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-primary truncate capitalize">
          {title}
        </p>
        <p className="text-sm/5 text-text-secondary truncate">
          {subtitle}
        </p>
      </div>
    </button>
  )
}

interface AddressSuggestionProps {
  address: DeliveryAddress
  onSelect: () => void
}

export function AddressSuggestion({ address, onSelect }: AddressSuggestionProps) {
  return (
    <button
      onClick={onSelect}
      className="
        w-full px-4 py-3 flex items-center gap-3
        text-left
        hover:bg-surface-tertiary
        transition-colors
        focus-visible:outline-none focus-visible:bg-surface-tertiary
      "
    >
      <div className="size-10 flex items-center justify-center rounded-lg bg-surface-tertiary text-text-secondary">
        <MapPinIcon className="size-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-primary truncate">
          {address.street}
        </p>
        <p className="text-sm/5 text-text-secondary truncate">
          {address.suburb} {address.state} {address.postcode}
        </p>
      </div>
    </button>
  )
}
